import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * RightDrawer — 60%-width slide-in panel from the right. Replaces dialog/
 * modal usage across the app for a more workspace-y, dockable feel.
 *
 * Props:
 *   open         boolean
 *   onClose      () => void
 *   title        ReactNode  — left side of header
 *   subtitle     ReactNode  — second line of header
 *   icon         ReactNode  — optional icon (lucide) for the header
 *   accent       string     — header bg colour (defaults to tenant primary)
 *   widthClass   string     — override the 60% default (e.g. for receipts)
 *   headerExtras ReactNode  — right-aligned chips/buttons in the header
 *   footer       ReactNode  — sticky footer (action buttons row)
 *   printableId  string     — id wrapped around body for window.print() use
 *   children     ReactNode  — body content
 *   data-testid  string
 *
 * The drawer is dismissable via Esc + overlay click. Body scroll is locked
 * while open so background interactions can't shift focus.
 */
export default function RightDrawer({
  open,
  onClose,
  title,
  subtitle,
  icon,
  accent,
  widthClass = 'w-full md:w-[60vw] max-w-[1100px]',
  headerExtras,
  footer,
  printableId,
  children,
  ...rest
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  const bg = accent || '#0f766e';

  return (
    <div className="fixed inset-0 z-50 print:static print:bg-white" data-testid={rest['data-testid'] || 'right-drawer'}>
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-zinc-900/55 backdrop-blur-sm transition-opacity print:hidden"
        onClick={onClose}
        aria-label="Close drawer"
      />
      {/* Panel */}
      <aside
        className={`absolute right-0 top-0 h-full bg-white shadow-2xl flex flex-col ${widthClass} print:relative print:w-full print:shadow-none`}
        style={{ animation: 'rdSlideIn 220ms cubic-bezier(0.22,1,0.36,1)' }}
        role="dialog"
        aria-modal="true"
      >
        <style>{`
          @keyframes rdSlideIn {
            from { transform: translateX(100%); opacity: 0.4; }
            to   { transform: translateX(0%);   opacity: 1; }
          }
          @media print {
            .rd-no-print { display: none !important; }
          }
        `}</style>

        {/* HEADER */}
        <header
          className="relative text-white px-7 py-5 flex-shrink-0 rd-header"
          style={{ background: `linear-gradient(135deg, ${bg} 0%, ${bg}dd 55%, ${bg}aa 100%)` }}
        >
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
               style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '16px 16px' }} />
          <div className="relative flex items-start gap-3">
            {icon && (
              <div className="h-11 w-11 rounded-xl bg-white/15 ring-1 ring-white/30 flex items-center justify-center flex-shrink-0">
                {icon}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] opacity-80 mb-0.5">Workspace</div>
              <h2 className="text-xl font-extrabold tracking-tight truncate"
                  style={{ fontFamily: 'Manrope' }}
                  data-testid="rd-title">
                {title}
              </h2>
              {subtitle && (
                <div className="text-sm opacity-90 mt-0.5 truncate" data-testid="rd-subtitle">
                  {subtitle}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 rd-no-print flex-shrink-0">
              {headerExtras}
              <button
                onClick={onClose}
                className="h-9 w-9 rounded-lg bg-white/15 hover:bg-white/25 transition flex items-center justify-center"
                aria-label="Close"
                data-testid="rd-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </header>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto bg-zinc-50/40" id={printableId}>
          {children}
        </div>

        {/* FOOTER */}
        {footer && (
          <footer className="flex-shrink-0 border-t bg-white px-7 py-3 flex items-center justify-end gap-2 rd-no-print">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}
